import { collectIPQSDeviceData } from "../IPQS/IPQSutils";
import { collectThreatMetrixDeviceData } from "../ThreatMetrix/ThreatMetrixUtils";
import { collectFingerprintDeviceData } from "../fingerprint/fingerprintUtils";
import { VerificationApiClient } from "../ServerApi/VerificationApiClient";
import { type DatabaseId, type ProgramTheme } from "../types/types";

type DeviceDataMetadata = {
  fpSessionId?: string;
};

const collectionsByVerificationId = new Map<string, Promise<DeviceDataMetadata>>();

/**
 * @description Collects device signals from all enabled fraud providers (ThreatMetrix, IPQS, Fingerprint)
 *              and tags them with a verificationId so the backend can look them up. Returns metadata
 *              that should be passed on the next API request. Memoized by verificationId so repeat callers
 *              use the same result without re-calling fraud providers.
 *              Used internally when programTheme is already available.
 */
export const collectDeviceData = (
  programTheme: ProgramTheme,
  verificationId: string,
): Promise<DeviceDataMetadata> => {
  const existing = collectionsByVerificationId.get(verificationId);
  if (existing) {
    return existing;
  }

  const collection = (async () => {
    const [, , fpSessionId] = await Promise.all([
      collectThreatMetrixDeviceData(programTheme, verificationId),
      collectIPQSDeviceData(programTheme, verificationId),
      collectFingerprintDeviceData(programTheme, verificationId),
    ]);
    return fpSessionId ? { fpSessionId } : {};
  })();

  collectionsByVerificationId.set(verificationId, collection);
  return collection;
};

/**
 * @public
 * @description Fetches the programTheme and collects device signals from all enabled fraud providers.
 *              Returns metadata (e.g. fpSessionId) that consumers should pass on the collect API request.
 *              Must be awaited before making subsequent API calls.
 */
export const collectDeviceProfile = async (
  verificationId: DatabaseId,
  programId: DatabaseId,
): Promise<DeviceDataMetadata> => {
  const programTheme = await VerificationApiClient.fetchProgramTheme(programId);
  return collectDeviceData(programTheme, verificationId);
};
